import {useEffect, useRef, useMemo, useState} from 'react'
import * as Comlink from 'comlink'
import get from 'lodash/get'
import debounce from 'lodash/debounce'
import {getMockupWorker} from '../webworkers/helpers/customShape'
import {useSelectedVariantContext} from '../components/step2/context/SelectedVariantContext'
import {handleCaptureArtworkWithFilter} from '../components/step2/context/SelectedVariantContext/helper'
import useEditorSafeZone from '../hooks/useEditorSafeZone'
import {hasFeatureTag} from '../helpers/settings'
const EXPORT_DIR = 'Mockup-Custom_Shape_2-side_Printed_Wood_Bookshelf_Sign_1772692537'
const ASSET_BASE_URL = `http://localhost:9001/${EXPORT_DIR}`

/**
 * Helper function to process node replacements and calculate updates
 * Hoisted outside component to prevent recreation on every render (js-cache-function-results)
 *
 * @param {Object} node - Document node to process
 * @param {string} namePattern - Pattern to match in node name (e.g., 'cut-side', 'print-side')
 * @param {Object} imageData - Image data with src, width, height
 * @param {Object} updates - Updates object to populate
 */
const processNodeUpdates = (node, namePattern, imageData, updates) => {
    const name = node.name || ''
    if (node.src && name.includes(namePattern)) {
        let bbox = node.mask?.bbox || node.bbox
        if (!bbox) {
            bbox = {
                left: 0,
                right: imageData.width || 0,
                top: 0,
                bottom: imageData.height || 0,
            }
        }

        const bboxWidth = bbox.right - bbox.left
        const bboxHeight = bbox.bottom - bbox.top
        const scaleX = bboxWidth / imageData.width
        const scaleY = bboxHeight / imageData.height
        const scale = Math.min(scaleX, scaleY)

        const newBbox = {
            left: bbox.left,
            top: bbox.top,
            right: bbox.left + imageData.width * scale,
            bottom: bbox.top + imageData.height * scale,
        }

        updates[node.id] = {
            src: imageData.src,
            bbox: newBbox,
            mask: {
                bbox: newBbox,
            },
        }
    }
    if (node.children) {
        node.children.forEach((child) => processNodeUpdates(child, namePattern, imageData, updates))
    }
}

/**
 * Hook to manage Pixi custom shape preview rendering
 *
 * @returns {Object} { pixiCanvasRef, isReady }
 */
export function usePreviewCustomShape() {
    const {
        state: {selectedSide, loadingImageLayer, selectedAttribute, designs, pickerAttributes, productCreatedType},
    } = useSelectedVariantContext()

    const pixiCanvasRef = useRef(null)
    const workerApiRef = useRef(null)
    const tmpCanvasRef = useRef(null)

    const [printSide, setPrintSide] = useState(null)
    const [isReady, setIsReady] = useState(false)
    const [isUpdating, setIsUpdating] = useState(false)

    const {safeZone: cutSide} = useEditorSafeZone()

    /**
     * NOTE: Only working if the store has "custom_shape_step2" feature tag and the product mockup type is "custom_shape"
     */
    const isCustomShape = productCreatedType === 'custom_shape' && hasFeatureTag('custom_shape_step2')
    console.log({productCreatedType, isCustomShape})
    const documentJson = useMemo(() => {
        if (!selectedAttribute || !selectedSide) return null
        return get(selectedAttribute, ['mockup', 'preview_v2', selectedSide, 'document'])
    }, [selectedAttribute, selectedSide])

    const updateRef = useRef()

    useEffect(() => {
        updateRef.current = async () => {
            if (selectedSide === 'All' || selectedSide === 'Safezone') return
            if (loadingImageLayer) return
            if (!workerApiRef.current || !documentJson) return

            try {
                setIsUpdating(true)
                const updates = {}

                if (cutSide?.src) processNodeUpdates(documentJson, 'cut-side', cutSide, updates)
                if (printSide?.src) processNodeUpdates(documentJson, 'print-side', printSide, updates)

                if (Object.keys(updates).length > 0) {
                    await workerApiRef.current.update(updates)
                } else {
                    await workerApiRef.current.render()
                }
            } catch (error) {
                console.error('[Pixi Worker] Error within update:', error)
            } finally {
                setIsUpdating(false)
            }
        }
    })

    // Memoize the debounced execution function so it persists across renders
    const debounceUpdate = useMemo(
        () =>
            debounce(() => {
                if (updateRef.current) updateRef.current()
            }, 500),
        [],
    )

    /**
     * Initialize Pixi worker and setup canvas
     */
    useEffect(() => {
        if (!isCustomShape || !documentJson || !pixiCanvasRef.current) return

        let isMounted = true

        async function initPixiWorker() {
            try {
                const api = await getMockupWorker()
                if (!isMounted) return
                workerApiRef.current = api

                const canvas = pixiCanvasRef.current
                const container = canvas.parentElement
                const {width: w, height: h} = container?.getBoundingClientRect() ?? {
                    width: 900,
                    height: 700,
                }
                const width = Math.round(w) || 900
                const height = Math.round(h) || 700

                canvas.width = width
                canvas.height = height

                const offscreen = canvas.transferControlToOffscreen()
                await api.init(Comlink.transfer(offscreen, [offscreen]), width, height)

                await api.loadDocument({
                    documentJson,
                    baseUrl: ASSET_BASE_URL,
                })

                if (isMounted) {
                    setIsReady(true)
                    // Update handles the `loadingImageLayer` check globally via `updateRef`
                    debounceUpdate()
                }
            } catch (err) {
                console.error('[Pixi Worker] Error:', err)
            }
        }

        initPixiWorker()

        // Cleanup
        return () => {
            isMounted = false
            setIsReady(false)
            if (workerApiRef.current) {
                workerApiRef.current.dispose()
                workerApiRef.current = null
            }
            debounceUpdate.cancel()
        }
    }, [isCustomShape, documentJson, debounceUpdate])

    /**
     * Setup resize observer for responsive canvas
     */
    useEffect(() => {
        if (!isCustomShape) return

        const container = pixiCanvasRef.current?.parentElement
        if (!container) return

        let timeoutId = null
        const observer = new ResizeObserver((entries) => {
            if (!entries[0] || !workerApiRef.current) return

            const {width, height} = entries[0].contentRect
            if (width && height) {
                clearTimeout(timeoutId)
                const timeoutCallback = () =>
                    workerApiRef.current?.resize({
                        width: Math.round(width),
                        height: Math.round(height),
                    })
                timeoutId = setTimeout(timeoutCallback, 150)
            }
        })

        observer.observe(container)
        return () => {
            observer.disconnect()
            clearTimeout(timeoutId)
        }
    }, [isCustomShape])

    /**
     * Trigger update when dependencies change
     */
    useEffect(() => {
        if (!isCustomShape) return

        debounceUpdate()

        return () => {
            debounceUpdate.cancel()
        }
    }, [isCustomShape, loadingImageLayer, cutSide?.src, printSide?.src, debounceUpdate])

    /**
     * Effect to capture print-side artwork automatically
     */
    useEffect(() => {
        if (!isCustomShape || loadingImageLayer) return
        if (selectedSide === 'All' || selectedSide === 'Safezone') return

        const capturePrintSide = () => {
            const attribute = pickerAttributes.find((attr) => attr._id === selectedAttribute._id)
            if (!attribute) return

            const stageRef = attribute.ref[selectedSide]
            const safeZoneData = attribute.safeZone[selectedSide]

            if (!stageRef || !safeZoneData) return

            const canvas = handleCaptureArtworkWithFilter(
                {
                    stageRef,
                    safeZone: safeZoneData,
                },
                {
                    excludeByName: ['background'],
                    pixelRatio: 1,
                },
            )

            if (canvas) {
                if (!tmpCanvasRef.current) {
                    tmpCanvasRef.current = document.createElement('canvas')
                }
                const tmpCanvas = tmpCanvasRef.current

                if (tmpCanvas.width !== canvas.width || tmpCanvas.height !== canvas.height) {
                    tmpCanvas.width = canvas.width
                    tmpCanvas.height = canvas.height
                }

                const ctx = tmpCanvas.getContext('2d')

                ctx.clearRect(0, 0, canvas.width, canvas.height)
                ctx.drawImage(canvas, 0, 0)

                setPrintSide({
                    src: tmpCanvas.toDataURL('image/png'),
                    width: canvas.width,
                    height: canvas.height,
                })
            }
        }

        const timeoutId = setTimeout(capturePrintSide, 300)
        return () => clearTimeout(timeoutId)
    }, [isCustomShape, pickerAttributes, designs, selectedAttribute, loadingImageLayer, selectedSide])

    return {
        pixiCanvasRef,
        isCustomShape,
        isReady,
        isUpdating,
    }
}
